import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useUiStore } from '@/stores/ui';

/**
 * Global keyboard shortcuts — spec §8.5.
 *
 * Nothing fires while the user is typing in a field or a rich-text editor, and
 * `G then <key>` is a real chord with a one-second window.
 */
const CHORD_WINDOW_MS = 1000;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.closest('[contenteditable="true"]') !== null
  );
}

export function useGlobalShortcuts(): void {
  const navigate = useNavigate();
  const setPalette = useUiStore((s) => s.setCommandPaletteOpen);
  const setQuickAdd = useUiStore((s) => s.setQuickAddOpen);
  const setNewTask = useUiStore((s) => s.setNewTaskOpen);
  const setShortcutSheet = useUiStore((s) => s.setShortcutSheetOpen);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);

  const chordAt = useRef<number>(0);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const mod = event.metaKey || event.ctrlKey;

      // ⌘K works even inside a field — it is how you escape to search.
      if (mod && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPalette(true);
        return;
      }

      if (isTypingTarget(event.target)) return;

      // ⌘⇧N is the full modal (V3 R3); ⌘N stays the fast path.
      if (mod && event.shiftKey && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        setNewTask(true);
        return;
      }
      if (mod && event.key.toLowerCase() === 'n') {
        event.preventDefault();
        setQuickAdd(true);
        return;
      }
      if (mod && event.key === '/') {
        event.preventDefault();
        toggleSidebar();
        return;
      }
      if (mod && event.shiftKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        setTheme(theme === 'dark' ? 'light' : 'dark');
        return;
      }
      if (event.key === '?' && !mod) {
        event.preventDefault();
        setShortcutSheet(true);
        return;
      }

      if (mod || event.altKey) return;

      // `G` opens a chord; the next key within a second is the destination.
      const now = Date.now();
      if (event.key.toLowerCase() === 'g') {
        chordAt.current = now;
        return;
      }
      if (now - chordAt.current > CHORD_WINDOW_MS) return;

      const destinations: Record<string, string> = {
        d: '/my-day',
        i: '/inbox',
        p: '/projects',
        t: '/my-tasks',
        n: '/notes',
        w: '/workload',
      };
      const destination = destinations[event.key.toLowerCase()];
      if (destination) {
        event.preventDefault();
        chordAt.current = 0;
        navigate(destination);
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, setPalette, setQuickAdd, setShortcutSheet, setTheme, theme, toggleSidebar]);
}
